import { createContext, useEffect, useContext } from 'react';
import useLaunchDarkly, {
  clearLaunchDarklyCache,
} from '@sentara-health/featuremanagement';
import { GlobalContext } from './ContextProvider';
import { Proxy, setLocalStorageBoolean, sendSessionProperties } from 'common';
import { isAvMed } from 'context';

export const FeatureToggleContext = createContext({} as any);

const FeatureToggleProvider = ({ children }: any) => {
  const { userEncodedData, getFeature, setFeature, loginMemberId, setAccountType } = useContext(GlobalContext);
  const { apiData, initService } = useLaunchDarkly();

  useEffect(() => {
    clearLaunchDarklyCache();
    if (userEncodedData.length > 0) {
      const tokenId = isAvMed()
        ? process.env.REACT_APP_AVMED_LAUNCH_DARKLY_KEY
        : process.env.REACT_APP_LAUNCH_DARKLY_KEY;

      if (tokenId) {
        initService(userEncodedData, tokenId, 1);
      }
    }
  }, [userEncodedData, initService]);

  useEffect(() => {
    // get all features and respective flag for toggle from getAllFeatureFlags mock API
    const getFeaturesAndFlags = async () => {
      if (userEncodedData.length > 0 && Array.isArray(apiData)) {
        if (apiData && apiData.length > 0) {
          let featureData: any = {};
          apiData.forEach((data: any) => {
            let key = Object.keys(data)[0];
            let values = Object.values(data)[0];
            featureData[key] = values ?? false;
          });
          setFeature((prev: any) => ({ ...prev, ...featureData }));
          setLocalStorageBoolean(Proxy.updateFeature, true);
        } else {
          // silently fallback to the initial state
        }
      }
    };
    getFeaturesAndFlags();
  }, [apiData, userEncodedData]);

  // Track member type for analytics based on feature flags
  // Will be revisiting this logic once we have a more robust solution for analytics using Mod Api's
  useEffect(() => {
    if (loginMemberId && userEncodedData.length > 0) {
      try {
        const parsedUserData = JSON.parse(userEncodedData);
        // Skip analytics for anonymous users
        if (parsedUserData?.key !== 'anonymous') {
          let memberType = null;
          if (getFeature?.HomeAddressMedicaid) {
            memberType = 'medicaid';
          } else if (getFeature?.HomeAddressCommerical) {
            memberType = 'commercial';
          } else if (
            getFeature?.HomeAddressMedicare 
          ) {
            memberType = 'medicare';
          } else if (getFeature?.HomeAddressDSNP) {
            memberType = 'dsnp';  
          } else if (
            getFeature?.HomeAddressIndividOff ||
            getFeature?.HomeAddressIndividOn
          ) {
            memberType = 'individual';
          }
          // Send member type to Dynatrace
          if (memberType) {
            setAccountType(memberType);
            sendSessionProperties(undefined, undefined, {
              member_type: memberType,
            });
          }
        }
      } catch (error) {
        console.error('[Dynatrace] Failed to send session properties:', error);
      }
    }
  }, [getFeature, loginMemberId, userEncodedData]);

  return (
    <FeatureToggleContext.Provider value={getFeature}>
      {children}
    </FeatureToggleContext.Provider>
  );
};

export default FeatureToggleProvider;
